import { useEffect, useState } from 'react';
import { Room, RoomEvent, Track } from 'livekit-client';

export const captureSizes: Record<string, {width: number; height: number}> = {
  '90p': {width:160,height:90}, '180p': {width:320,height:180}, '216p': {width:384,height:216},
  '240p': {width:426,height:240}, '360p': {width:640,height:360}, '480p': {width:854,height:480},
  '720p': {width:1280,height:720}, '960p': {width:1280,height:960}, '1080p': {width:1920,height:1080},
  '1440p': {width:2560,height:1440}, '2160p': {width:3840,height:2160},
};
export interface CameraResolutionPolicy {
  meetingId: string; roomSid: string; revision: string; layout: 'custom'|'smart'|'grid'|'focus'; automatic: boolean;
  mainIdentity: string; mainSource: string; mainCameraIdentities: string[];
  targets: Record<string, {participantSid: string; configuredResolution: string; resolution: string; fps?: number; configuredFps?: number}>;
}
export function readCameraResolutionPolicy(room: Room, meetingId: string): CameraResolutionPolicy | null {
  try {
    const policy = JSON.parse(room.metadata || '{}').cameraResolutionPolicy as CameraResolutionPolicy;
    if (policy?.meetingId !== meetingId || !policy.targets || !/^\d+$/.test(policy.revision)) return null;
    return policy;
  } catch { return null; }
}

const senderScales = new WeakMap<RTCRtpSender, Map<string, number>>();
export async function applyCameraDimensions(track: {mediaStreamTrack: MediaStreamTrack; sender?: RTCRtpSender}, size: {width:number;height:number; frameRate?: number}) {
  const media = track.mediaStreamTrack, old = media.getConstraints();
  if (size.frameRate) old.frameRate = {ideal: size.frameRate, max: size.frameRate};
  try {
    await media.applyConstraints({...old, width: {ideal:size.width, max:size.width}, height: {ideal:size.height, max:size.height}, aspectRatio: {ideal:size.width/size.height}});
  } catch (error) {
    if ((error as Error)?.name !== 'OverconstrainedError') throw error;
    // Some browsers/cameras cannot capture at 240p. Keep their supported capture size,
    // then cap only this camera sender's encoded dimensions (never audio/screen share).
    await media.applyConstraints({...old, width: {ideal:size.width}, height: {ideal:size.height}, aspectRatio: {ideal:size.width/size.height}});
  }
  const actual = media.getSettings();
  const factor = Math.max(1, (actual.width ?? size.width)/size.width, (actual.height ?? size.height)/size.height);
  const sender = track.sender;
  if (factor > 1 && !sender) throw new Error('Camera cannot reach target size and sender is not ready');
  if (sender) {
    const params = sender.getParameters();
    let bases = senderScales.get(sender);
    if (!bases) { bases = new Map(); senderScales.set(sender, bases); }
    let changed = false;
    params.encodings?.forEach((encoding, index) => {
      if (size.frameRate && encoding.maxFramerate !== size.frameRate) { encoding.maxFramerate = size.frameRate; changed = true; }
      const key = encoding.rid || String(index);
      if (!bases!.has(key)) bases!.set(key, encoding.scaleResolutionDownBy ?? 1);
      const scale = bases!.get(key)! * factor;
      if (Math.abs((encoding.scaleResolutionDownBy ?? 1) - scale) > 0.001) { encoding.scaleResolutionDownBy = scale; changed = true; }
    });
    if (changed) await sender.setParameters(params);
  }
}

/** Server-authored Room metadata is durable on reconnect; peer data packets cannot control capture. */
export function useCameraResolutionPolicy(room: Room, meetingId?: string) {
  const [policy, setPolicy] = useState<CameraResolutionPolicy | null>(null);
  useEffect(() => {
    if (!meetingId) { setPolicy(null); return; }
    const originalResolution = room.options.videoCaptureDefaults?.resolution;
    let preferredSize: {width: number; height: number; frameRate?: number} | undefined;
    let disposed = false, running = false, queued = false, newest = 0;
    let appliedTrack: MediaStreamTrack | undefined, appliedResolution = '';
    const update = async () => {
      if (running) { queued = true; return; }
      running = true;
      try {
        do {
          queued = false;
          if (room.state !== 'connected') continue;
          const state = readCameraResolutionPolicy(room, meetingId);
          const target = state?.targets[room.localParticipant.identity];
          if (!state || !target || target.participantSid !== room.localParticipant.sid || Number(state.revision) < newest) continue;
          newest = Number(state.revision);
          if (!disposed) setPolicy(state);
          preferredSize = {...captureSizes[target.configuredResolution], ...(target.configuredFps ? {frameRate: target.configuredFps} : {})};
          const dimensions = captureSizes[target.resolution];
          if (!dimensions) continue;
          const size = {...dimensions, ...(target.fps ? {frameRate: target.fps} : {})};
          const qualityKey = `${target.resolution}/${target.fps ?? "legacy"}`;
          // Used when the user next enables an absent/stopped camera; never enables it here.
          room.options.videoCaptureDefaults = {...room.options.videoCaptureDefaults, resolution: {...room.options.videoCaptureDefaults?.resolution, ...size}};
          const publication = room.localParticipant.getTrackPublication(Track.Source.Camera);
          const media = publication?.track?.mediaStreamTrack;
          if (!media || publication?.isMuted || media.readyState !== 'live' || disposed) continue;
          if (media === appliedTrack && appliedResolution === qualityKey) continue;
          try {
            // Change ONLY camera dimensions: preserve deviceId, FPS, mute, audio and screen share.
            await applyCameraDimensions(publication!.track!, size);
            if (!disposed && room.localParticipant.getTrackPublication(Track.Source.Camera)?.track?.mediaStreamTrack === media) {
              appliedTrack = media; appliedResolution = qualityKey;
              console.info('[CAMERA_RESOLUTION]', target.resolution, media.getSettings());
            }
          } catch (error) {
            // Keep the live track and retry on next update; do not unpublish or toggle camera.
            console.warn('[CAMERA_RESOLUTION] apply failed', error);
          }
        } while (queued && !disposed);
      } finally { running = false; }
    };
    const trigger = () => { void update(); };
    const events = [RoomEvent.RoomMetadataChanged, RoomEvent.Connected, RoomEvent.Reconnected,
      RoomEvent.LocalTrackPublished, RoomEvent.TrackUnmuted];
    events.forEach(e => room.on(e, trigger));
    const timer = window.setInterval(trigger, 5000);
    trigger();
    return () => {
      disposed = true; window.clearInterval(timer); events.forEach(e => room.off(e, trigger));
      room.options.videoCaptureDefaults = {...room.options.videoCaptureDefaults,
        resolution: preferredSize ? {...room.options.videoCaptureDefaults?.resolution, ...preferredSize} : originalResolution};
    };
  }, [room, meetingId]);
  return policy;
}
